/**
 * CompletePatrolConfirmDialog
 *
 * Confirmation dialog shown before completing a patrol,
 * especially when checkpoints are incomplete.
 *
 * Shows:
 * - Warning about incomplete checkpoints
 * - Optional note field for dispatcher
 * - Confirm/Cancel actions
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

export interface CompletePatrolConfirmDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog should close */
  onClose: () => void;
  /** Callback when completion is confirmed */
  onConfirm: (note?: string) => void;
  /** Whether completion is in progress */
  isCompleting?: boolean;
  /** Number of incomplete checkpoints */
  incompleteCount: number;
  /** Total number of checkpoints */
  totalCount: number;
  /** Guard name for context */
  guardName: string;
  /** Site name for context */
  siteName: string;
  /** Error message to display */
  error?: string | null;
}

const CompletePatrolConfirmDialog: React.FC<CompletePatrolConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isCompleting = false,
  incompleteCount,
  totalCount,
  guardName,
  siteName,
  error = null,
}) => {
  const [note, setNote] = useState('');

  const handleConfirm = useCallback(() => {
    const trimmedNote = note.trim();
    onConfirm(trimmedNote || `Completed by dispatcher - ${incompleteCount} checkpoints skipped`);
  }, [note, onConfirm, incompleteCount]);

  const handleClose = useCallback(() => {
    if (!isCompleting) {
      setNote('');
      onClose();
    }
  }, [isCompleting, onClose]);

  const completedCount = totalCount - incompleteCount;
  const hasIncomplete = incompleteCount > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="complete-patrol-confirm-title"
    >
      <DialogTitle id="complete-patrol-confirm-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasIncomplete && (
            <WarningAmberIcon sx={{ color: 'warning.main' }} />
          )}
          <span>Complete Patrol</span>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {hasIncomplete ? (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              This patrol has <strong>{incompleteCount}</strong> incomplete checkpoint
              {incompleteCount !== 1 ? 's' : ''} out of {totalCount} total.
            </DialogContentText>
            <Box
              sx={{
                p: 2,
                mb: 2,
                bgcolor: 'warning.lighter',
                borderRadius: 1,
                borderLeft: 3,
                borderColor: 'warning.main',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>Guard:</strong> {guardName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Site:</strong> {siteName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Progress:</strong> {completedCount}/{totalCount} checkpoints
              </Typography>
            </Box>
            <DialogContentText sx={{ mb: 2 }}>
              Are you sure you want to complete this patrol on behalf of the guard?
            </DialogContentText>
          </>
        ) : (
          <DialogContentText sx={{ mb: 2 }}>
            Complete this patrol for <strong>{guardName}</strong> at <strong>{siteName}</strong>?
          </DialogContentText>
        )}

        <TextField
          label="Note (optional)"
          placeholder="Add a note about why this patrol is being completed..."
          multiline
          rows={2}
          fullWidth
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={isCompleting}
          variant="outlined"
          size="small"
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isCompleting}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isCompleting}
          variant="contained"
          color={hasIncomplete ? 'warning' : 'primary'}
          startIcon={isCompleting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isCompleting ? 'Completing...' : hasIncomplete ? 'Complete Anyway' : 'Complete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompletePatrolConfirmDialog;
